import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  getMyVault, listMessages, listRecipients, rescheduleMessage, withdrawMessage,
  type MessageListItem,
} from '../lib/vault/vaultApi';
import './vault.css';

/**
 * The messages sealed for one recipient — what the member left, when it will
 * find them, and the two escape hatches. The list itself is the point: before
 * this screen, a sealed message vanished from view and every row read a
 * hardcoded "No messages yet". Knowing a message CAN be revised is what lets a
 * person record honestly, so reschedule + withdraw live here beside each one.
 */
export default function MessagesScreen() {
  const { api } = useAuth();
  const { id: recipientId } = useParams();
  const [messages, setMessages] = useState<MessageListItem[] | null>(null);
  const [recipientName, setRecipientName] = useState<string>('');
  const [error, setError] = useState('');

  async function load() {
    setError('');
    try {
      const dash = await getMyVault(api);
      const [msgs, recips] = await Promise.all([
        listMessages(api, dash.vault.id),
        listRecipients(api, dash.vault.id),
      ]);
      setRecipientName(recips.recipients.find((r) => r.id === recipientId)?.full_name ?? 'this person');
      setMessages(msgs.messages.filter((m) => m.recipient_id === recipientId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load these messages.');
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [recipientId]);

  if (error) {
    return (
      <section className="vault">
        <p role="alert" className="vault__alert">{error}</p>
        <p className="vault__alt"><Link className="la-btn-secondary" to="/">Back to vault</Link></p>
      </section>
    );
  }
  if (messages === null) {
    return <section className="vault"><p role="status" className="vault__kicker">Opening…</p></section>;
  }

  return (
    <section className="vault">
      <p className="vault__kicker">For {recipientName}</p>
      <h1 className="vault__title">What you've sealed.</h1>
      {messages.length === 0 ? (
        <p className="vault__empty">Nothing sealed for them yet. When you record and seal a message, it will wait here.</p>
      ) : (
        <div className="mlist">
          {messages.map((m) => <MessageCard key={m.id} m={m} onChanged={() => void load()} />)}
        </div>
      )}
      <p className="vault__alt" style={{ marginTop: 24 }}><Link className="la-btn-secondary" to="/">Back to vault</Link></p>
    </section>
  );
}

function whenLabel(m: MessageListItem): string {
  const t = m.trigger;
  if (m.status === 'delivered') return `Delivered${m.delivered_at ? ' ' + new Date(m.delivered_at).toLocaleDateString() : ''}`;
  if (m.status === 'withdrawn') return 'Withdrawn';
  if (t.type === 'date' && t.trigger_date) return `Arrives ${new Date(t.trigger_date).toLocaleDateString()}`;
  if (t.type === 'age' && t.target_age != null) return `Arrives when they turn ${t.target_age}`;
  if ((t.type === 'life_event' || t.type === 'inheritance') && t.life_event_type) return `Arrives at: ${t.life_event_type.replace(/_/g, ' ')}`;
  if (t.type === 'emergency_only') return 'Held until you are gone';
  return 'Scheduled';
}

function MessageCard({ m, onChanged }: { m: MessageListItem; onChanged: () => void }) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [newDate, setNewDate] = useState('');

  const canChange = m.status !== 'delivered' && m.status !== 'withdrawn';
  const today = new Date().toISOString().slice(0, 10);

  async function doReschedule() {
    if (!newDate) { setError('Choose a new date.'); return; }
    setBusy(true); setError('');
    try {
      await rescheduleMessage(api, m.id, { trigger_date: newDate });
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reschedule.');
    } finally { setBusy(false); }
  }

  async function doWithdraw() {
    setBusy(true); setError('');
    try {
      await withdrawMessage(api, m.id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not withdraw.');
    } finally { setBusy(false); }
  }

  return (
    <div className={`mcard${m.status === 'withdrawn' ? ' mcard--withdrawn' : ''}`}>
      <div className="mcard__body">
        <div className="mcard__title">{m.title?.trim() || `A ${m.format} message`}</div>
        <div className="mcard__meta">{whenLabel(m)}{m.is_emergency_only ? ' · emergency-only' : ''}</div>
        {error && <div className="rrow__error" role="alert">{error}</div>}
        {editing && (
          <div className="mcard__edit">
            <input className="auth__input" type="date" min={today} value={newDate} onChange={(e) => setNewDate(e.target.value)} aria-label="New delivery date" />
            <button className="la-btn-primary" onClick={() => void doReschedule()} disabled={busy}>{busy ? 'Saving…' : 'Save new date'}</button>
            <button className="la-btn-secondary" onClick={() => { setEditing(false); setError(''); }} disabled={busy}>Cancel</button>
          </div>
        )}
      </div>
      {canChange && !editing && (
        <div className="mcard__actions">
          <button className="la-btn-secondary" onClick={() => setEditing(true)}>Reschedule</button>
          <button className="la-btn-secondary" onClick={() => void doWithdraw()} disabled={busy}>{busy ? 'Withdrawing…' : 'Withdraw'}</button>
        </div>
      )}
    </div>
  );
}
